<template>
  <div>
    <div class="layout-sidebar-section">
      <div
        class="layout-sidebar-section-title"
        @click="$emit('toggleSection', 'fillSection')"
      >
        <strong>Fill</strong>
        <b-icon :icon="isOpen.fillSection ? 'chevron-up' : 'chevron-down'" />
      </div>
      <transition>
        <b-collapse :visible="isOpen.fillSection">
          <div>
            <div>
              <b-form-group
                v-show="isNotLine"
                label="Fill Type"
                label-cols="4"
              >
                <b-form-radio-group
                  v-model="fillType"
                  :options="fillTypeOptions"
                  class="fill-type-selector"
                  @change="onFillTypeChange"
                />
              </b-form-group>

              <!-- Solid Fill Controls -->
              <div class="d-flex align-items-baseline position-relative">
                <b-form-group
                  v-show="isNotLine && fillType === 'solid'"
                  label="Fill Color"
                  label-cols="4"
                  label-class="text-nowrap pr-3"
                  class="flex-grow-1"
                >
                  <ColorPicker
                    :value="typeof (target.fill) === 'string' ? target.fill : '#000000'"
                    :themes="themes"
                    copy
                    @input="(value) => {
                      onChange('bgColor', value);
                    }"
                  />
                </b-form-group>
                
                <b-button
                  v-if="isShodShowBrandColor"
                  variant="link"
                  class="p-0 no-focus-outline add-to-color-table"
                  @click="showBrandColors"
                >
                  Add to Color Table
                </b-button>
              </div>

              <!-- Gradient Controls -->
              <template v-if="fillType === 'gradient'">
                <b-form-group
                  label="Gradient Type"
                  label-cols="4"
                >
                  <b-form-radio-group
                    v-model="gradientType"
                    :options="gradientTypeOptions"
                    @change="updateGradient"
                  />
                </b-form-group>

                <b-form-group
                  v-if="gradientType === 'linear'"
                  label="Direction"
                  label-cols="4"
                >
                  <b-form-select
                    v-model="gradientDirection"
                    :options="gradientDirectionOptions"
                    @change="updateGradient"
                  />
                </b-form-group>

                <b-form-group
                  label="Gradient Colors"
                  label-cols="4"
                >
                  <div
                    v-for="(stop, index) in colorStops"
                    :key="index"
                    class="d-flex mb-2"
                  >
                    <ColorPicker
                      :value="stop.color"
                      :themes="themes"
                      copy
                      @input="updateColorStop(index, $event)"
                    />
                    <b-input
                      v-model.number="stop.offset"
                      type="number"
                      min="0"
                      max="100"
                      step="1"
                      class="ml-2"
                      style="width: 80px"
                      @input="updateGradient"
                    />
                    <b-button
                      v-if="index > 1"
                      variant="danger"
                      size="sm"
                      class="ml-2"
                      @click="removeColorStop(index)"
                    >
                      <b-icon icon="trash" />
                    </b-button>
                  </div>
                  <b-button
                    v-if="colorStops.length < 5"
                    variant="primary"
                    size="sm"
                    @click="addColorStop"
                  >
                    Add Color Stop
                  </b-button>
                </b-form-group>
              </template>
            </div>
          </div>
        </b-collapse>
      </transition>
    </div>
    <div class="layout-sidebar-section">
      <div
        class="layout-sidebar-section-title"
        style="cursor: pointer;"
        @click="$emit('toggleSection', 'borderSettingsSection')"
      >
        <strong>Border Settings</strong>
        <b-icon :icon="isOpen.borderSettingsSection ? 'chevron-up' : 'chevron-down'" />
      </div>
      <transition>
        <b-collapse :visible="isOpen.borderSettingsSection">
          <div>
            <div>
              <b-form-group
                label="Border"
                label-cols="4"
              >
                <div class="d-flex">
                  <ColorPicker
                    :value="target.stroke"
                    :themes="themes"
                    copy
                    @input="onChange('strokeColor', $event)"
                  />
                  <b-button
                    v-b-tooltip.hover
                    variant="primary"
                    size="sm"
                    title="Use background color"
                    @click="onChange('strokeColor', target.fill)"
                  >
                    <b-icon icon="back" />
                  </b-button>
                </div>
              </b-form-group>
              
              <b-form-group
                :label="borderWidthName()"
                :disabled="target.locked"
              >
                <b-input-group>
                  <b-input
                    :value="target.strokeWidth"
                    :min="0"
                    :max="maxStrokeWidth"
                    type="range"
                    @input="onChange('thickness', $event)"
                  />
                  <template #append>
                    <b-input
                      :value="target.strokeWidth"
                      :min="0"
                      :max="maxStrokeWidth"
                      type="number"
                      @input="onChange('thickness', $event)"
                    />
                  </template>
                </b-input-group>
              </b-form-group>
              <b-form-group
                v-if="!isNotLine && isNotFieldTypeCutLine"
                label="Line length"
                :disabled="target.locked"
              >
                <b-input-group>
                  <b-input
                    :value="parseInt(target.coords[2])"
                    :min="1"
                    :max="2000"
                    type="range"
                    @input="onChange('coords', $event)"
                  />
                  <template #append>
                    <b-input
                      :value="parseInt(target.coords[2])"
                      :min="1"
                      :max="2000"
                      type="number"
                      @input="onChange('coords', $event)"
                    />
                  </template>
                </b-input-group>
              </b-form-group>
              <b-form-group
                v-if="target.fieldType !== FIELD_TYPE_SAFE_ZONE && isNotLine"
                label="Rounding"
                :disabled="target.locked"
              >
                <b-input-group>
                  <b-input
                    v-model.number="localRadius"
                    :min="0"
                    :max="maxRounding"
                    type="range"
                    @input="onRadiusInput"
                  />
                  <template #append>
                    <b-input
                      v-model.number="localRadius"
                      :min="0"
                      :max="maxRounding"
                      type="number"
                      @input="onRadiusInput"
                    />
                  </template>
                </b-input-group>
              </b-form-group>
            </div>
          </div>
        </b-collapse>
      </transition>
    </div>
    <div class="layout-sidebar-section">
      <div
        class="layout-sidebar-section-title"
        @click="$emit('toggleSection', 'opacitySettingsSection')"
      >
        <strong>Opacity Settings</strong>
        <b-icon :icon="isOpen.opacitySettingsSection ? 'chevron-up' : 'chevron-down'" />
      </div>
      <transition>
        <b-collapse :visible="isOpen.opacitySettingsSection">
          <div>
            <div>
              <b-form-group label="Opacity">
                <b-input-group>
                  <b-input
                    :value="target.opacity"
                    :min="0"
                    :max="maxOpacity"
                    step="0.01"
                    type="range"
                    @input="onChange('opacity', $event)"
                  />
                  <template #append>
                    <b-input
                      :value="target.opacity"
                      :min="0"
                      :max="maxOpacity"
                      step="0.01"
                      type="number"
                      @input="onChange('opacity', $event)"
                    />
                  </template>
                </b-input-group>
              </b-form-group>
              <b-form-group
                v-show="isNotLine"
                label="Fill opacity"
              >
                <b-input-group>
                  <b-input
                    v-model="fillOpacity"
                    :min="0"
                    :max="1"
                    step="0.01"
                    type="range"
                    @input="onChangeFillOpacity"
                  />
                  <template #append>
                    <b-input
                      v-model="fillOpacity"
                      :min="0"
                      :max="1"
                      step="0.01"
                      type="number"
                      @input="onChangeFillOpacity"
                    />
                  </template>
                </b-input-group>
              </b-form-group>
              <b-form-group label="Border opacity">
                <b-input-group>
                  <b-input
                    v-model="strokeOpacity"
                    :min="0"
                    :max="maxOpacity"
                    step="0.01"
                    type="range"
                    @input="onChangeStrokeOpacity"
                  />
                  <template #append>
                    <b-input
                      v-model="strokeOpacity"
                      :min="0"
                      :max="maxOpacity"
                      step="0.01"
                      type="number"
                      @input="onChangeStrokeOpacity"
                    />
                  </template>
                </b-input-group>
              </b-form-group>

              <b-form-row v-if="isRelatedFieldChangeAvailable">
                <b-col>
                  <b-form-group
                    label="Related field"
                  >
                    <b-form-select
                      :value="target.relatedField?.field"
                      :options="relatedTemplateFields"
                      @change="onChange('changeRelatedField2', $event)"
                    />
                  </b-form-group>
                </b-col>

                <b-col>
                  <b-form-group label="Margin">
                    <b-form-input
                      :value="target.relatedField?.margin"
                      type="number"
                      min="0"
                      step="1"
                      @input="onChange('changeMargin', $event)"
                      @dblclick="selectValueOnDblclick"
                    />
                  </b-form-group>
                </b-col>
              </b-form-row>
            </div>
          </div>
        </b-collapse>
      </transition>
    </div>
    <template
      v-if="targetTypeRectangleOrAdditionalFieldRectangleOrSaveZone || isLine"
    >
      <label
        v-if="isLine"
      >Position</label>
      <PositionAndSize
        :w="target.width * target.scaleX"
        :h="target.height * target.scaleY"
        :x="target.left"
        :y="target.top"
        :show-keep-aspect-ratio="false"
        :show-size="!isLine"
        :is-open="isOpen"
        @toggleSection="toggleSectionHandler"
        @input="onChange"
      />
    </template>
    <div class="layout-sidebar-section">
      <div
        class="layout-sidebar-section-title"
        @click="$emit('toggleSection', 'actionSection')"
      >
        <strong>Actions</strong>
        <b-icon :icon="isOpen.actionSection ? 'chevron-up' : 'chevron-down'" />
      </div>
      <transition>
        <b-collapse :visible="isOpen.actionSection">
          <div>
            <div
              v-if="isAlignAvailable"
              class="shape-editor-actions d-flex my-2"
              style="grid-gap: 1rem;"
            >
              <button
                v-b-tooltip.hover
                title="Set to template width"
                @click="onChange('setTemplateSizeByWidth')"
              >
                <img src="/img/icons/expand.svg">
              </button>
              <button
                v-b-tooltip.hover
                title="Center to template width"
                @click="onChange('centerToTemplateWidth')"
              >
                <img
                  src="/img/icons/align-center.png"
                  alt="align-center-icon"
                >
              </button>
              <button
                v-b-tooltip.hover
                title="Left align to template"
                @click="onChange('leftAlignToTemplate')"
              >
                <img
                  src="/img/icons/align-left.png"
                  alt="align-left-icon"
                >
              </button>
              <button
                v-b-tooltip.hover
                title="Right align to template"
                @click="onChange('rightAlignToTemplate')"
              >
                <img
                  src="/img/icons/align-right.png"
                  alt="align-right-icon"
                >
              </button>
            </div>
            
            <div class="position-fixed apply-to-all-shapes-section">
              <ApplyToAllShapesDropdown
                v-if="isApplyToAllAvailable"
                :target="target"
              />
            </div>

            <b-form-group
              v-if="isOrientationAvailable"
              label="Orientation"
            >
              <b-form-radio-group
                v-model="orientation"
                :options="orientationOptions"
                name="orientation"
                @change="onChangeOrientation"
              />
            </b-form-group>

            <b-form-group v-if="isOrientationAvailable">
              <b-form-checkbox
                v-model="includeLineWidth"
                label="Include line width"
                name="includeLineWidth"
                @change="onChangeIncludeLineWidth"
              >
                Include line width
              </b-form-checkbox>
            </b-form-group>
          </div>
        </b-collapse>
      </transition>
    </div>

    <b-button
      v-if="!isNonDeletable"
      variant="danger"
      @click="onChange('delete')"
    >
      <b-icon icon="trash" />
      Delete
    </b-button>

    <LockToggle />
  </div>
</template>

<script>
import { editorControlEventBroker, EVENTS } from '@frontend/group/modules/editor-control-popup/event-broker'
import ColorPicker from './components/ColorPicker.vue'
import LockToggle from '@frontend/components/modules/layouts-page/EditorControl/components/LockToggle.vue'
import {FIELD_TYPE_CUT_LINE, FIELD_TYPE_LINE, FIELD_TYPE_SAFE_ZONE} from '@frontend/constants/type-fields-of-template';
import PositionAndSize from "@frontend/components/modules/layouts-page/EditorControl/components/PositionAndSize.vue";
import {
  ADDITIONAL_RECTANGLE_PREFIX_ID,
  RECTANGLE_PREFIX_ID,
  SAFE_ZONE_PREFIX_ID
} from "@frontend/constants/templates-field-prefixes";
import { createGradient, GRADIENT_TYPES, GRADIENT_DIRECTIONS, DEFAULT_COLOR_STOPS, FILL_TYPE_OPTIONS } from '@frontend/utils/gradient-helper';
import {brandColorsEventBroker, EVENTS as BRAND_COLORS_EVENTS} from "@frontend/group/modules/brand-colors/event-broker";
import mixinInput from '@frontend/components/input-mixins'
import {RELATED_FIELD_TYPES} from "@frontend/constants/related-fields";
import {mapGetters} from "vuex";
import {ORIENTATION} from "@frontend/constants/orientation";
import ApplyToAllShapesDropdown
  from "@frontend/components/modules/layouts-page/EditorControl/components/ApplyToAllShapes.vue";
const NON_DELETABLE_SHAPES_PREFIXES = [
    SAFE_ZONE_PREFIX_ID
];

export default {
  name: 'ShapeEditorControl',

  components: {
    ApplyToAllShapesDropdown,
    PositionAndSize,
    LockToggle,
    ColorPicker,
  },
  mixins: [mixinInput],
  props: {
    target: {
      type: Object,
      default: () => {},
    },
    themes: {
      type: Array,
      default: () => [],
    },
    isOpen: {
      type: Object,
      default: () => ({
        actionSection: true,
        fillSection: true,
        borderSettingsSection: true,
        opacitySettingsSection: true,
      }),
    },
  },

  data: () => ({
    FIELD_TYPE_SAFE_ZONE,
    FIELD_TYPE_LINE,
    maxRounding: 500,
    maxStrokeWidth: 100,
    maxOpacity: 1,
    selectedPropertyKeys: [],
    orientation: null,
    includeLineWidth: null,
    applyToAllOptions: [
      { text: 'Background', value: 'bgColor' },
      { text: 'Stroke', value: 'strokeColor' },
    ],
    orientationOptions: [
      { text: 'Horizontal', value: ORIENTATION.HORIZONTAL },
      { text: 'Vertical', value: ORIENTATION.VERTICAL }
    ],
    fillOpacity: 1,
    strokeOpacity: 1,
    fillType: 'solid',
    gradientType: 'linear',
    gradientDirection: 'to right',
    colorStops: DEFAULT_COLOR_STOPS,
    gradientDirectionOptions: GRADIENT_DIRECTIONS,
    gradientTypeOptions: GRADIENT_TYPES,
    fillTypeOptions: FILL_TYPE_OPTIONS,
    localRadius: 0,
  }),
  computed: {
    ...mapGetters('layout', [
      'selectedInstance',
    ]),
    isNotFieldTypeLine() {
      return this.target.fieldType !== FIELD_TYPE_LINE;
    },
    isNotFieldTypeCutLine() {
      return this.target.fieldType !== FIELD_TYPE_CUT_LINE;
    },
    isNotLine() {
      return this.isNotFieldTypeCutLine && this.isNotFieldTypeLine;
    },
    isLine() {
      return this.target.fieldType === FIELD_TYPE_LINE;
    },
    isNonDeletable() {
      return NON_DELETABLE_SHAPES_PREFIXES.some(prefix => this.target?.element_id.startsWith(prefix));
    },
    isAlignAvailable() {
      return this.isNotFieldTypeCutLine;
    },
    isApplyToAllAvailable() {
      return this.isNotFieldTypeCutLine;
    },
    isOrientationAvailable() {
      return !this.isNotFieldTypeCutLine;
    },
    isRelatedFieldChangeAvailable() {
      return this.isNotFieldTypeCutLine;
    },
    isShodShowBrandColor() {
      return this.target.id.startsWith(RECTANGLE_PREFIX_ID) && this.target.isShodShowBrandColor;
    },
    targetTypeRectangleOrAdditionalFieldRectangleOrSaveZone() {
      return this.target.id.startsWith(RECTANGLE_PREFIX_ID) || this.target.id.startsWith(ADDITIONAL_RECTANGLE_PREFIX_ID) || this.target.fieldType === FIELD_TYPE_SAFE_ZONE;
    },
    relatedTemplateFields() {
      const fields = this.selectedInstance.template.fields
          .filter(field => RELATED_FIELD_TYPES.includes(field.type) && field.name !== this.target.templateField?.name)
          .map(field => field.name);

      return [
        { value: '', text: 'Select related field' },
        ...fields
      ];
    },

  },
  watch: {
    target() {
      this.init();
    },
    'target.radius'(newVal) {
      if (!this.target.setRadius) {
        return;
      }
      const numeric = typeof newVal === 'number' ? newVal : Number(newVal);
      this.localRadius = isNaN(numeric) ? 0 : numeric;
    },
  },
  mounted() {
    this.init()
  },
  methods: {
    init() {
      const prepareOpacity = (value) => {
        return value == 1.00 ? 1 : value;
      }

      if (!this.target) {
        return;
      }

      if (this.target?.stroke) {
        this.strokeOpacity = prepareOpacity(this.hexToOpacity(this.target.stroke));
      }
      if (this.target?.fillType === 'gradient') {        
        let gradientData = JSON.parse(this.target.gradient);

        this.fillType = 'gradient';
        this.gradientType = gradientData.type;

        if (gradientData.direction) {
          this.gradientDirection = gradientData.direction;
        }
        if (gradientData.colorStops) {
          this.colorStops = gradientData.colorStops.map(stop => ({
            offset: stop.offset * 100,
            color: stop.color
          }));
        }
      } else {
        this.fillType = 'solid';
        if (this.target?.fill) {
          this.fillOpacity = prepareOpacity(this.hexToOpacity(this.target.fill));
        }
      }
      this.orientation = this.target.orientation || null;
      this.includeLineWidth = this.target.includeLineWidth || null;
      this.updateMaxRounding();

      const currentRadius = (this.target.setRadius ? this.target.radius : this.target.rx) || 0;
      this.localRadius = Math.min(currentRadius, this.maxRounding);
    },
    updateMaxRounding() {
      if (!this.target) {
        this.maxRounding = 0;
        return;
      }

      const width = (this.target.width || 0) * (this.target.scaleX || 1);
      const height = (this.target.height || 0) * (this.target.scaleY || 1);
      const minSide = Math.min(width || 0, height || 0);

      this.maxRounding = minSide > 0 ? Math.floor(minSide / 2) : 0;

      if (this.localRadius > this.maxRounding) {
        this.localRadius = this.maxRounding;
      }
    },
    onRadiusInput(value) {
      const numeric = typeof value === 'number' ? value : Number(value);
      const safeValue = isNaN(numeric) ? 0 : numeric;
      const clamped = Math.max(0, Math.min(this.maxRounding, safeValue));

      this.localRadius = clamped;
      this.onChange('radius', clamped);
    },
    onChange: _.debounce(function (action, value) {
      editorControlEventBroker.fire(EVENTS.SHAPE_CHANGED, {
        action,
        value,
        keys: [...this.selectedPropertyKeys],
      })
    }, 15),
    onChangeFillOpacity(value)  {
      this.onChange('bgColor', this.opacityToHex(this.target.fill, value))
    },
    onChangeStrokeOpacity(value){
      this.onChange('strokeColor', this.opacityToHex(this.target.stroke, value))
    },
    applyToAll([action]) {
      this.onChange(action, action === 'bgColor' ? this.target.fill : this.target.stroke);
    },
    onChangeOrientation() {
      this.onChange('orientation', this.orientation);
    },
    onChangeIncludeLineWidth() {
      this.onChange('includeLineWidth', this.includeLineWidth);
    },
    toggleSectionHandler(sectionKey) {
      this.$emit('toggleSection', sectionKey);
    },
    hexToOpacity(hexColor) {
      // Check if the string has a valid length
      if (hexColor.length <= 7) { // Format #RRGGBB (no alpha channel)
        return 1; // Fully opaque
      } else if (hexColor.length === 9) { // Format #RRGGBBAA
        const alphaHex = hexColor.slice(-2); // Last two characters represent the alpha channel
        const decimalAlpha = parseInt(alphaHex, 16); // Convert HEX to decimal
        return (decimalAlpha / 255).toFixed(2); // Normalize to 0-1 range and round to 1 decimal place
      } else {
        throw new Error("HEX string must be in the format #RRGGBB or #RRGGBBAA");
      }
    },
    opacityToHex(hexColor, opacity) {
      // Validate opacity is in the range 0 to 1
      if (opacity < 0 || opacity > 1) {
        throw new Error("Opacity must be a decimal value between 0 and 1");
      }

      // Convert opacity to HEX
      const alphaHex = Math.round(opacity * 255).toString(16).padStart(2, '0').toUpperCase();

      // Handle HEX color without alpha channel (#RRGGBB)
      if (hexColor.length < 7) {
        return '#00000000'; // Append the alpha channel
      }
      if (hexColor.length === 7) {
        return hexColor + alphaHex; // Append the alpha channel
      }

      // Handle HEX color with alpha channel (#RRGGBBAA)
      if (hexColor.length === 9) {
        return hexColor.slice(0, 7) + alphaHex; // Replace the alpha channel
      }

      // Invalid HEX format
      throw new Error("HEX string must be in the format #RRGGBB or #RRGGBBAA");
    },
    onFillTypeChange() {
      if (this.fillType === 'gradient') {
        this.updateGradient();
      } else {
        this.onChange('bgColor', "#FFFFFF")
      }
    },
    updateGradient() {
      if (!this.target) return;

      const gradientData = {
        type: this.gradientType,
        direction: this.gradientDirection,
        colorStops: this.colorStops.map(stop => ({
          offset: stop.offset / 100,
          color: stop.color
        }))
      };
      let gradient = createGradient(gradientData);

      this.target.set('fill', gradient);
      this.target.canvas.renderAll();

      // Dispatch the change event so that the save button is enabled
      this.onChange('gradient', gradientData);
    },
    updateColorStop(index, color) {
      this.colorStops[index].color = color;
      this.updateGradient();
    },
    addColorStop() {
      this.colorStops.push({
        offset: 100,
        color: '#ffffff'
      });
      this.updateGradient();
    },
    removeColorStop(index) {
      this.colorStops.splice(index, 1);
      this.updateGradient();
    },
    borderWidthName() {
      if (this.isNotLine) {
        return 'Border width';
      }
      
      return 'Line width';
    },
    showBrandColors() {
      brandColorsEventBroker.fire(BRAND_COLORS_EVENTS.INIT, {
        instance_id: this.target.canvas.instance_id,
      });
    },
  },
}
</script>

<style scoped lang="scss">
.add-to-color-table {
  position: absolute;
  top: 5px;
  right: 5px;
}
.no-focus-outline:focus {
  outline: none;
  box-shadow: none;
}
.apply-to-all-shapes-section{
  bottom: 44px;
  z-index: 999;
}
</style>
